import { Autocomplete, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography, useTheme } from "@mui/material";
import { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { DekoxIcon } from "src/components";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { Button, IconButton, TooltipShortcut } from "@itk/mui-components";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { configuratorActions } from "../../configurator-store";
import { TGroupFieldKey, TGroupSortBy, TSortDirection } from "../../configurator-types";
import { getGroupFieldLabel } from "../../configurator-utils";

const mapStateToProps = (state: RootState) => ({
  activeTab: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.activeTab,
  groupFields: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.groupFields,
  groupSortBy: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.groupSortBy,
  groupSortDirection: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.groupSortDirection
});

const ownActions = {
  setGroupFields: configuratorActions.setGroupFields,
  setGroupSortBy: configuratorActions.setGroupSortBy,
  setGroupSortDirection: configuratorActions.setGroupSortDirection,
  setActiveOptionsPanel: configuratorActions.setActiveOptionsPanel
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

// Products supports the full breakdown; Configurations a smaller subset — same pool as before this became multi-select
const PRODUCTS_GROUP_FIELD_OPTIONS: TGroupFieldKey[] = ["label", "configurationLabel", "productName", "productType", "configurationName", "missingInfo", "invalidInfo", "date"];
const CONFIGURATIONS_GROUP_FIELD_OPTIONS: TGroupFieldKey[] = ["configurationLabel", "missingInfo", "invalidInfo"];

const DRAG_TYPE = "GROUP_FIELD_ROW";
type TDragItem = { index: number };

// A single row in the hierarchy-order list — both a drag source and a drop target, following
// react-dnd's standard sortable-list pattern (reorder live on hover, not just on drop).
const DraggableGroupFieldRow = ({ index, label, moveField }: { index: number; label: string; moveField: (from: number, to: number) => void }) => {
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);

  const [, drop] = useDrop<TDragItem>({
    accept: DRAG_TYPE,
    hover(item) {
      if (!ref.current || item.index === index) return;
      moveField(item.index, index);
      item.index = index;
    }
  });

  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPE,
    item: (): TDragItem => ({ index }),
    collect: monitor => ({ isDragging: monitor.isDragging() })
  });

  drag(drop(ref));

  return (
    <Stack
      ref={ref}
      direction="row"
      alignItems="center"
      gap={1}
      px={1}
      py={0.5}
      sx={{
        opacity: isDragging ? 0.4 : 1,
        border: `1px solid ${theme.palette.customColors.grey}`,
        borderRadius: 1,
        cursor: "move",
        backgroundColor: theme.palette.customColors.white
      }}
    >
      <DekoxIcon icon="moreVertical" fontSize="small" sx={{ color: theme.palette.customColors.darkGrey }} />
      <Typography variant="body2">{label}</Typography>
    </Stack>
  );
};

const GroupByBase = (props: Props) => {
  const { activeTab, groupFields, groupSortBy, groupSortDirection, setGroupFields, setGroupSortBy, setGroupSortDirection, setActiveOptionsPanel } = props;

  const configuratorPageT = useConfiguratorPageT();
  const genericTranslationsT = useGenericTranslationsT();

  const groupFieldOptions = activeTab === "PRODUCTS" ? PRODUCTS_GROUP_FIELD_OPTIONS : CONFIGURATIONS_GROUP_FIELD_OPTIONS;

  const moveField = (fromIndex: number, toIndex: number) => {
    const reordered = [...groupFields];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setGroupFields(reordered);
  };

  // Back to configuratorReducer's initialState defaults for all three group-related fields —
  // selections still apply instantly, Reset is just a shortcut to "no grouping" plus default group-sort
  const handleReset = () => {
    setGroupFields([]);
    setGroupSortBy("alphabetical");
    setGroupSortDirection("asc");
  };

  return (
    <Stack overflow="hidden" width="100%" py={2} flex={1} minHeight={0}>
      <Stack flexShrink={0} px={3} direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" fontWeight={600}>
          {configuratorPageT.groupBy}
        </Typography>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Button color="error" t={genericTranslationsT} k={genericTranslationsK} field="reset" variant="text" size="small" onClick={handleReset} />
          <TooltipShortcut shortcut={{ action: () => setActiveOptionsPanel(null), shortCutKey: "g", modifier: "alt" }}>
            <IconButton k={genericTranslationsK.close} size="small" onClick={() => setActiveOptionsPanel(null)}>
              <DekoxIcon icon="doubleArrowRight" fontSize="small" />
            </IconButton>
          </TooltipShortcut>
        </Stack>
      </Stack>
      <Stack overflow="auto" flex={1} minHeight={0} gap={2} mt={2} px={3} py={2}>
        {/* which fields to group by; order here doesn't matter — the reorder list below is the source of hierarchy order */}
        <Autocomplete<TGroupFieldKey, true>
          multiple
          value={groupFields}
          onChange={(_, values) => setGroupFields(values)}
          options={groupFieldOptions}
          getOptionLabel={field => getGroupFieldLabel(configuratorPageT, field)}
          isOptionEqualToValue={(option, value) => option === value}
          size="small"
          renderInput={params => <TextField {...params} label={configuratorPageT.groupBy} />}
        />

        {/* hierarchy order, top level first — only meaningful once 2+ fields are selected */}
        {groupFields.length > 1 && (
          <Stack gap={0.5}>
            {groupFields.map((field, index) => (
              <DraggableGroupFieldRow key={field} index={index} label={getGroupFieldLabel(configuratorPageT, field)} moveField={moveField} />
            ))}
          </Stack>
        )}

        {/* how the group buckets themselves (not the items inside them) are ordered — independent of item-level Sort By/Order */}
        <FormControl size="small" fullWidth>
          <InputLabel>{configuratorPageT.sortGroupsBy}</InputLabel>
          <Select value={groupSortBy} label={configuratorPageT.sortGroupsBy} onChange={e => setGroupSortBy(e.target.value as TGroupSortBy)}>
            <MenuItem value="alphabetical">{configuratorPageT.sortGroupsByAlphabetical}</MenuItem>
            <MenuItem value="count">{configuratorPageT.sortGroupsByCount}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel>{configuratorPageT.sortOrder}</InputLabel>
          <Select value={groupSortDirection} label={configuratorPageT.sortOrder} onChange={e => setGroupSortDirection(e.target.value as TSortDirection)}>
            <MenuItem value="asc">{genericTranslationsT.sortAscending}</MenuItem>
            <MenuItem value="desc">{genericTranslationsT.sortDescending}</MenuItem>
          </Select>
        </FormControl>
      </Stack>
    </Stack>
  );
};

export const GroupBy = connect(mapStateToProps, ownActions)(GroupByBase);
